import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import { Sparkles, Loader2, School, Trees, Bus, Coffee, Route, Navigation,
          Beer, ShoppingBag, Church, HeartPulse, Info, ExternalLink, Clock,
          Phone, Globe, MapPin, Flame, MapPinned } from 'lucide-react';
import { CatchmentOverlay, CatchmentDisclaimer } from './CatchmentOverlay';

// Default Leaflet icon fix (unchanged from previous version).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Purple pin for the primary listing.
const primaryIcon = new L.Icon({
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize:      [25, 41],
  iconAnchor:    [12, 41],
});

// Small circular divIcon for nearby lovinahome listings.
const nearbyIcon = L.divIcon({
  className: 'lh-nearby-pin',
  html:      '<div style="width:14px;height:14px;border-radius:50%;background:#7042cc;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35)"></div>',
  iconSize:  [14, 14],
  iconAnchor:[7, 7],
});

// Grey pin for sold-price comparables (postcode-clustered, jittered).
const soldIcon = L.divIcon({
  className: 'lh-sold-pin',
  html:      '<div style="width:9px;height:9px;border-radius:50%;background:#6b7280;border:1.5px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>',
  iconSize:  [9, 9],
  iconAnchor:[5, 5],
});

// Amenity icons — pill-shaped marker with a Lucide-style SVG glyph
// inside a coloured circle with a white border. Much easier to scan
// at a glance than plain coloured dots.
const AMENITY_SVGS = {
  schools:  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m4 6 8-4 8 4"/><path d="m18 10 4 2v3"/><path d="M14 22v-4a2 2 0 0 0-4 0v4"/><path d="M18 5v17"/><path d="M6 5v17"/><circle cx="12" cy="9" r="2"/></svg>',
  parks:    '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22V15"/><path d="M9 8a3 3 0 1 1 6 0c0 1.5-.6 2.5-1.5 3.2A4 4 0 0 1 12 15a4 4 0 0 1-1.5-3.8C9.6 10.5 9 9.5 9 8Z"/><path d="M7 15a5 5 0 0 1 10 0"/></svg>',
  transit:  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M8 6v6"/><path d="M16 6v6"/><path d="M2 12h20"/><path d="M18 18h1a2 2 0 0 0 2-2v-3H3v3a2 2 0 0 0 2 2h1"/><path d="M6 22v-4"/><path d="M18 22v-4"/></svg>',
  food:     '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M17 8h1a4 4 0 0 1 0 8h-1"/><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z"/><path d="M6 2v3"/><path d="M10 2v3"/><path d="M14 2v3"/></svg>',
  pubs:     '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M17 11h1a3 3 0 0 1 0 6h-1"/><path d="M9 12v6"/><path d="M13 12v6"/><path d="M14 7.5c-1 0-1.4.5-2 1s-1 1-2 1-1.4-.5-2-1-1-1-2-1"/><path d="M5 8v11a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8H5Z"/></svg>',
  shops:    '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>',
  worship:  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M18 7v13a1 1 0 0 1-1 1h-4v-6a2 2 0 1 0-4 0v6H5a1 1 0 0 1-1-1V7l8-5Z"/><path d="M10 5V2"/><path d="M14 5V2"/><path d="M10 3.5h4"/></svg>',
  health:   '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.5-1.4 3-3.4 3-5.5a5.5 5.5 0 0 0-10-3 5.5 5.5 0 0 0-10 3c0 2.1 1.5 4.1 3 5.5l7 7Z"/><path d="M3.2 12h4.8l2-3 3 6 2-3h4.8"/></svg>',
};
const amenityIcon = (colour, category) => L.divIcon({
  className: 'lh-amenity-pin',
  html:      `<div style="width:26px;height:26px;border-radius:50%;background:${colour};border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;">${AMENITY_SVGS[category] || ''}</div>`,
  iconSize:  [26, 26],
  iconAnchor:[13, 13],
});

// Static area→coords fallback lookup.
const STATIC_COORDS = {
  'ranelagh':[53.3200,-6.2550], 'grand canal dock':[53.3420,-6.2380],
  'malahide':[53.4508,-6.1540], 'rathmines':[53.3220,-6.2650],
  'ballsbridge':[53.3267,-6.2290], 'clondalkin':[53.3230,-6.3950],
  'dublin':[53.3498,-6.2603], 'cork':[51.8969,-8.4863],
  'galway':[53.2707,-9.0568], 'limerick':[52.6638,-8.6267],
  'waterford':[52.2593,-7.1101],
  'london':[51.5074,-0.1278], 'clapham':[51.4626,-0.1383],
  'clapham north':[51.4669,-0.1334], 'manchester':[53.4808,-2.2426],
  'didsbury':[53.4178,-2.2329], 'edinburgh':[55.9533,-3.1883],
  'new town':[55.9568,-3.1986], 'cardiff':[51.4816,-3.1791],
  'pontcanna':[51.4863,-3.1968], 'belfast':[54.5973,-5.9301],
  'titanic quarter':[54.6080,-5.9106], 'birmingham':[52.4862,-1.8904],
  'glasgow':[55.8642,-4.2518], 'leeds':[53.8008,-1.5491],
  'liverpool':[53.4084,-2.9916], 'bristol':[51.4545,-2.5879],
};

const UK_NAMES = /^(UK|GB|United Kingdom|Great Britain|England|Scotland|Wales|Northern Ireland)$/i;
const UK_POSTCODE = /\b([A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})\b/i;

const extractUKPostcode = (location) => {
  const candidates = [location?.postcode, location?.eircode, location?.fullAddress, location?.address];
  for (const s of candidates) {
    if (!s) continue;
    const m = String(s).match(UK_POSTCODE);
    if (m) return m[1].toUpperCase().replace(/\s+/g, '');
  }
  return null;
};

const staticLookup = (location) => {
  const area = location.area?.toLowerCase() || '';
  const city = location.city?.toLowerCase() || '';
  const county = location.county?.toLowerCase() || '';
  return STATIC_COORDS[area] || STATIC_COORDS[city] || STATIC_COORDS[county] || null;
};

const fallbackCenter = (location) => (
  UK_NAMES.test(location?.country || '') ? [54.0, -2.5] : [53.3498, -6.2603]
);

const currencyFor = (country) => (
  UK_NAMES.test(country || '') ? '£'
  : /United States|USA|US/i.test(country || '') ? '$'
  : '€'
);

// Recenter helper — Leaflet doesn't respond to `center` after mount.
const Recenter = ({ lat, lng, zoom }) => {
  const map = useMap();
  useEffect(() => { map.setView([lat, lng], zoom); }, [lat, lng, zoom, map]);
  return null;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Amenity categories (OpenStreetMap Overpass API) ──────────────────────
// Each category maps to an Overpass filter, a pin colour, and a Lucide
// icon. Popup fields (address, opening_hours, website, phone,
// denomination, cuisine, brand, wheelchair) are extracted below and
// rendered in the marker popup so buyers can act on a POI without
// leaving the map.
const AMENITY_CATS = {
  schools:  { icon: School,      colour: '#f59e0b', label: 'Schools',   query: '["amenity"~"school|kindergarten|college|university"]' },
  parks:    { icon: Trees,       colour: '#16a34a', label: 'Parks',     query: '["leisure"~"park|garden|nature_reserve|playground"]' },
  transit:  { icon: Bus,         colour: '#0284c7', label: 'Transit',   query: '["public_transport"~"station|stop_position|platform"],["railway"~"tram_stop|station|halt|subway_entrance"],["highway"="bus_stop"]' },
  food:     { icon: Coffee,      colour: '#b45309', label: 'Food & Drink', query: '["amenity"~"cafe|restaurant|fast_food"]' },
  pubs:     { icon: Beer,        colour: '#a16207', label: 'Pubs',      query: '["amenity"~"pub|bar|biergarten"]' },
  shops:    { icon: ShoppingBag, colour: '#db2777', label: 'Shops',     query: '["shop"~"supermarket|convenience|bakery|butcher|greengrocer|mall|department_store"]' },
  worship:  { icon: Church,      colour: '#6d28d9', label: 'Places of worship', query: '["amenity"="place_of_worship"],["building"~"church|chapel|mosque|temple|synagogue|cathedral"]' },
  health:   { icon: HeartPulse,  colour: '#dc2626', label: 'Health',    query: '["amenity"~"pharmacy|clinic|hospital|doctors|dentist"]' },
};

// Haversine distance (metres) — used to add "X m away" / "X.X km away"
// to every POI popup so buyers see how far each pin is from the listing.
const _distMetres = (lat1, lng1, lat2, lng2) => {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
          + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};
const _fmtDist = (m) => m == null ? '' : (m < 950 ? `${Math.round(m)} m away` : `${(m / 1000).toFixed(1)} km away`);

// Nominatim's `display_name` sometimes repeats the same locality name
// ("Rochfortbridge, Rochfortbridge, ..."). Dedupe any repeated parts
// (consecutive or not) before slicing to a short label.
const _shortLabel = (raw) => {
  if (!raw) return '';
  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  const seen = new Set();
  const deduped = parts.filter((p) => {
    const k = p.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  return deduped.slice(0, 3).join(', ');
};

const fetchOverpass = async (lat, lng, category, radiusM = 1500) => {
  // Category queries may contain multiple OSM tag filters separated by
  // commas (e.g. `["amenity"="place_of_worship"],["building"~"church|…"]`).
  // Emit a `node`/`way` statement per clause so the Overpass union
  // captures buildings AND amenity-tagged features (needed for churches
  // that only have `building=church`, and Luas stops tagged
  // `railway=tram_stop` rather than `public_transport=*`).
  const clauses = AMENITY_CATS[category].query.split(/,(?=\[)/);
  const union = clauses.map((c) => (
    `node${c}(around:${radiusM},${lat},${lng});way${c}(around:${radiusM},${lat},${lng});`
  )).join('');
  const q = `[out:json][timeout:15];(${union});out center 60;`;
  const body = 'data=' + encodeURIComponent(q);
  const endpoints = [
    'https://overpass-api.de/api/interpreter',
    'https://overpass.kumi.systems/api/interpreter',
  ];
  for (const url of endpoints) {
    try {
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      if (!r.ok) continue;
      const d = await r.json();
      return (d.elements || []).map((el) => {
      const t = el.tags || {};
      const meta = [];
      // Enrich school pins with level, gender and rough capacity when
      // OSM has them tagged (no external ratings API needed).
      if (category === 'schools') {
        const level = t['isced:level'] || t['school:type'] || '';
        if (/1|2|primary/i.test(level)) meta.push('Primary');
        else if (/3|secondary|high/i.test(level)) meta.push('Secondary');
        if (t.operator_type === 'private' || t.fee === 'yes') meta.push('Private');
        else if (t.operator_type === 'public_authority' || t.operator_type === 'government') meta.push('Public');
        if (t.gender === 'female') meta.push('Girls');
        else if (t.gender === 'male') meta.push('Boys');
        else if (t.gender === 'mixed') meta.push('Mixed');
        if (t.capacity) meta.push(`~${t.capacity} pupils`);
      }
      if (category === 'transit') {
        if (t.railway === 'tram_stop' || t.tram === 'yes' || t.station === 'tram') meta.push('Luas / Tram');
        else if (t.railway === 'station' || t.railway === 'halt') meta.push('Rail');
        else if (t.railway === 'subway_entrance' || t.subway === 'yes' || t.station === 'subway') meta.push('Metro');
        else if (t.highway === 'bus_stop' || t.bus === 'yes') meta.push('Bus');
        if (t.network) meta.push(t.network);
        if (t.line)    meta.push(`Line ${t.line}`);
      }
      if (category === 'food' || category === 'pubs') {
        if (t.cuisine)  meta.push(t.cuisine.split(';')[0].replace(/_/g, ' '));
        if (t.takeaway === 'yes')  meta.push('Takeaway');
        if (t.outdoor_seating === 'yes') meta.push('Outdoor');
      }
      if (category === 'shops') {
        if (t.shop)  meta.push(t.shop.replace(/_/g, ' '));
        if (t.brand) meta.push(t.brand);
      }
      if (category === 'worship') {
        if (t.religion)     meta.push(t.religion.replace(/_/g, ' '));
        if (t.denomination) meta.push(t.denomination.replace(/_/g, ' '));
        if (!t.religion && t.building) meta.push(t.building.replace(/_/g, ' '));
      }
      if (category === 'health') {
        if (t.amenity) meta.push(t.amenity.replace(/_/g, ' '));
        if (t.healthcare) meta.push(t.healthcare.replace(/_/g, ' '));
      }
      if (t.wheelchair === 'yes')       meta.push('♿ Accessible');
      else if (t.wheelchair === 'limited') meta.push('♿ Limited');

      const plat = el.lat ?? el.center?.lat;
      const plng = el.lon ?? el.center?.lon;

      // Build street address from OSM addr:* tags when present.
      const houseNum = t['addr:housenumber'];
      const street   = t['addr:street'];
      const city     = t['addr:city'] || t['addr:town'] || t['addr:suburb'];
      const address  = [
        [houseNum, street].filter(Boolean).join(' '),
        city,
      ].filter(Boolean).join(', ');

      // Sensible fallback name for unnamed churches / tram stops
      const fallbackName = category === 'worship'
        ? (t.religion ? `${t.religion.replace(/_/g, ' ')} place of worship` : 'Place of worship')
        : (category === 'transit' && t.railway === 'tram_stop' ? 'Luas / Tram stop'
        : AMENITY_CATS[category].label);

      return {
        id: `${category}-${el.type}-${el.id}`,
        osmType:  el.type,     // 'node' | 'way' — needed for OSM deep-link
        osmId:    el.id,
        name:     t.name || fallbackName,
        lat:      plat,
        lng:      plng,
        meta,
        address,
        hours:    t.opening_hours || '',
        phone:    t.phone || t['contact:phone'] || '',
        website:  t.website || t['contact:website'] || '',
      };
    }).filter((p) => p.lat && p.lng)
      .map((p) => ({ ...p, _distM: _distMetres(lat, lng, p.lat, p.lng) }));
    } catch { /* try next mirror */ }
  }
  return [];
};

// ── Neighbourhood AI panel (right of the map) ────────────────────────────
const NeighbourhoodAI = ({ area, county, country, beds, propertyType }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!area) return;
    setLoading(true); setErr(null);
    const params = new URLSearchParams({ area, county: county || '', country: country || 'Ireland' });
    if (beds) params.set('beds', String(beds));
    if (propertyType) params.set('property_type', propertyType);
    fetch(`${BACKEND_URL}/api/ai/neighbourhood?${params.toString()}`)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(setData)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [area, county, country, beds, propertyType]);

  return (
    <div className="rounded-lg bg-gradient-to-br from-purple-50 via-white to-indigo-50 ring-1 ring-purple-100 p-4"
         data-testid="neighbourhood-ai">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#7042cc] mb-2">
        <Sparkles className="h-3.5 w-3.5" /> About this neighbourhood
      </div>
      {loading && (
        <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Writing a quick summary…
        </div>
      )}
      {err && !loading && (
        <p className="text-xs text-gray-500 italic">Couldn&apos;t fetch the neighbourhood summary right now.</p>
      )}
      {data && !loading && (
        <>
          <p className="text-sm text-gray-800 leading-relaxed mb-2">{data.blurb}</p>
          <ul className="space-y-1">
            {(data.bullets || []).map((b, i) => (
              <li key={i} className="text-[13px] text-gray-700 flex items-start gap-2">
                <span className="text-[#7042cc] mt-1">•</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
          {data.cached && (
            <p className="text-[10px] text-gray-400 mt-2 italic">Cached · updated periodically</p>
          )}
        </>
      )}
    </div>
  );
};

// ── Commute widget ──────────────────────────────────────────────────────
// Uses the OSM Foundation's public OSRM demo at routing.openstreetmap.de
// which hosts a SEPARATE profile per host (`/routed-car`, `/routed-bike`,
// `/routed-foot`). The alternative `router.project-osrm.org` only serves
// the car profile, which is why the old widget showed identical durations
// for all three modes.
//
// Destination text is sticky across listing views (localStorage) — a
// buyer scanning many homes almost always wants the same commute anchor.
const COMMUTE_DEST_STORAGE_KEY   = 'commute_check_destination_v1';
const COMMUTE_RECENT_STORAGE_KEY = 'commute_check_recent_v1';
const COMMUTE_RECENT_MAX = 5;

// Read / write the "last few destinations" chip list used by the widget.
const readRecent = () => {
  try {
    const raw = window.localStorage.getItem(COMMUTE_RECENT_STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string' && x.trim()) : [];
  } catch { return []; }
};
const writeRecent = (list) => {
  try {
    window.localStorage.setItem(COMMUTE_RECENT_STORAGE_KEY,
                                 JSON.stringify(list.slice(0, COMMUTE_RECENT_MAX)));
  } catch { /* private mode */ }
};

const CommuteWidget = ({ lat, lng, country, propertyId }) => {
  const [dest, setDest] = useState(() => {
    try { return window.localStorage.getItem(COMMUTE_DEST_STORAGE_KEY) || ''; }
    catch { return ''; }
  });
  const [routes, setRoutes] = useState(null);
  const [transitRow, setTransitRow] = useState(null);    // {minutes,km,unreachable}
  const [driveTraffic, setDriveTraffic] = useState(null); // {minutes,free_minutes,congestion,km}
  const [resolved, setResolved] = useState(null);  // {label, lat, lng}
  const [altMatches, setAltMatches] = useState([]);   // top-N Nominatim
  const [altOpen, setAltOpen] = useState(false);      // picker visible?
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [recent, setRecent] = useState(readRecent);
  const [suggests, setSuggests] = useState(null);          // Nominatim autocomplete dropdown
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedAnchor, setSavedAnchor] = useState(null);
  const autoTimer = useRef(null);

  // Map the listing's country onto an ISO-3166-1 alpha-2 code that
  // Nominatim can use to bias its search. Without this bias, a query
  // like "trinity college" from a Dublin property resolves to Trinity
  // College Cambridge (~500 km across the Irish Sea) — same bug that
  // made bike/walk report identical durations because no ferry-aware
  // route exists across the water.
  const countryBias = useMemo(() => {
    const c = (country || '').toLowerCase();
    if (c.includes('ireland') || c === 'ie') return 'ie';
    if (c.includes('united kingdom') || c === 'uk' || c === 'gb'
        || c.includes('england') || c.includes('scotland') || c.includes('wales')
        || c.includes('northern ireland')) return 'gb';
    if (c.includes('united states') || c === 'us' || c === 'usa') return 'us';
    return '';
  }, [country]);

  // Persist every keystroke so switching listings preserves the anchor.
  useEffect(() => {
    try {
      if (dest) window.localStorage.setItem(COMMUTE_DEST_STORAGE_KEY, dest);
      else      window.localStorage.removeItem(COMMUTE_DEST_STORAGE_KEY);
    } catch { /* ignore quota / private-mode failures */ }
  }, [dest]);

  // On mount: if the user has saved this specific listing WITH an
  // anchor pinned to it, pre-fill and auto-run so their next visit
  // opens straight to the commute they care about.
  useEffect(() => {
    if (!propertyId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/saved/${propertyId}/anchor`,
           { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (!d) return;
        setSaved(!!d.saved);
        if (d.anchor?.label) {
          setSavedAnchor(d.anchor);
          setDest(d.anchor.label);
        }
      })
      .catch(() => { /* ignore — non-critical */ });
  }, [propertyId]);

  // Debounced Nominatim autocomplete — fires as the buyer types.
  useEffect(() => {
    if (autoTimer.current) clearTimeout(autoTimer.current);
    const q = dest.trim();
    if (q.length < 3) { setSuggests(null); return; }
    autoTimer.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          format: 'json', limit: '5', q,
          ...(countryBias ? { countrycodes: countryBias } : {}),
        });
        const r = await fetch(
          `https://nominatim.openstreetmap.org/search?${params.toString()}`,
          { headers: { 'Accept-Language': 'en' } },
        );
        const arr = await r.json();
        setSuggests(Array.isArray(arr) ? arr : []);
      } catch { setSuggests([]); }
    }, 320);
    return () => clearTimeout(autoTimer.current);
  }, [dest, countryBias]);

  const runForLatLng = async (dlat, dlng, matchedLabel) => {
    setLoading(true); setErr(null); setRoutes(null); setTransitRow(null); setDriveTraffic(null);
    setResolved({ label: matchedLabel, lat: dlat, lng: dlng });
    try {
      // Kick off transit (TravelTime) + drive-traffic (HERE) alongside
      // OSRM so the widget populates all rows in parallel.
      const token = localStorage.getItem('token');
      const transitP = token
        ? fetch(`${process.env.REACT_APP_BACKEND_URL}/api/commute/transit-time`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ origin_lat: lat, origin_lng: lng,
                                     dest_lat: dlat, dest_lng: dlng }),
          }).then((r) => r.ok ? r.json() : null).catch(() => null)
        : Promise.resolve(null);
      const trafficP = token
        ? fetch(`${process.env.REACT_APP_BACKEND_URL}/api/commute/drive-traffic`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ origin_lat: lat, origin_lng: lng,
                                     dest_lat: dlat, dest_lng: dlng }),
          }).then((r) => r.ok ? r.json() : null).catch(() => null)
        : Promise.resolve(null);

      const profiles = [
        { key: 'car',  label: 'Drive', emoji: '🚗', host: 'https://routing.openstreetmap.de/routed-car'  },
        { key: 'bike', label: 'Cycle', emoji: '🚴', host: 'https://routing.openstreetmap.de/routed-bike' },
        { key: 'foot', label: 'Walk',  emoji: '🚶', host: 'https://routing.openstreetmap.de/routed-foot' },
      ];
      const results = await Promise.all(profiles.map(async (p) => {
        const url = `${p.host}/route/v1/driving/${lng},${lat};${dlng},${dlat}?overview=false`;
        try {
          const r = await fetch(url);
          if (!r.ok) return { ...p, minutes: null, km: null };
          const d = await r.json();
          const route = (d.routes || [])[0];
          if (!route) return { ...p, minutes: null, km: null };
          return { ...p, minutes: Math.round(route.duration / 60),
                          km: (route.distance / 1000).toFixed(1) };
        } catch { return { ...p, minutes: null, km: null }; }
      }));
      setRoutes(results);

      const transitData = await transitP;
      if (transitData && transitData.minutes != null) {
        setTransitRow({
          minutes: transitData.minutes,
          km: transitData.km != null ? transitData.km.toFixed?.(1) ?? String(transitData.km) : null,
          unreachable: false,
        });
      } else if (transitData && transitData.unreachable) {
        setTransitRow({ minutes: null, km: null, unreachable: true });
      }

      const trafficData = await trafficP;
      if (trafficData && trafficData.minutes != null) {
        setDriveTraffic({
          minutes:      trafficData.minutes,
          free_minutes: trafficData.free_minutes,
          congestion:   trafficData.congestion,
          km:           trafficData.km,
        });
      }

      // Push destination onto the "recent" chip list (dedup + cap).
      const q = dest.trim();
      if (q) {
        const next = [q, ...recent.filter((v) => v.toLowerCase() !== q.toLowerCase())]
                       .slice(0, COMMUTE_RECENT_MAX);
        setRecent(next); writeRecent(next);
      }
    } catch (e) {
      setErr(e.message || 'Could not calculate route');
    } finally { setLoading(false); }
  };

  const run = async () => {
    if (!dest.trim() || !lat || !lng) return;
    setSuggestOpen(false);
    setAltOpen(false);
    try {
      // 1) Geocode destination via Nominatim, biased to the property's
      //    country when we can infer one. Ask for top 3 hits so we
      //    can offer a "Not this place?" picker when the top result
      //    is wrong (e.g. "Trinity College" → Cambridge vs Dublin).
      const params = new URLSearchParams({
        format: 'json', limit: '3', q: dest,
        ...(countryBias ? { countrycodes: countryBias } : {}),
      });
      const gr = await fetch(
        `https://nominatim.openstreetmap.org/search?${params.toString()}`,
        { headers: { 'Accept-Language': 'en' } },
      );
      const gd = await gr.json();
      if (!gd?.[0]) { setErr('Address not found'); return; }
      setAltMatches(gd);
      const dlat = parseFloat(gd[0].lat), dlng = parseFloat(gd[0].lon);
      const shortLabel = _shortLabel(gd[0].display_name || dest);
      await runForLatLng(dlat, dlng, shortLabel);
    } catch (e) { setErr(e.message || 'Could not calculate route'); }
  };

  // Swap the resolved match to a different Nominatim candidate. Reuses
  // the same routing pipeline so all 4 rows + traffic recompute.
  const pickAltMatch = (alt) => {
    const label = _shortLabel(alt.display_name);
    setAltOpen(false);
    runForLatLng(parseFloat(alt.lat), parseFloat(alt.lon), label);
  };

  const pickSuggestion = (s) => {
    const label = _shortLabel(s.display_name);
    setDest(label);
    setSuggestOpen(false);
    runForLatLng(parseFloat(s.lat), parseFloat(s.lon), label);
  };

  const pickRecent = (q) => {
    setDest(q);
    setSuggestOpen(false);
    // Nominatim search then route — reuse `run`.
    setTimeout(() => run(), 0);
  };

  const pinToListing = async () => {
    if (!propertyId) return;
    const token = localStorage.getItem('token');
    if (!token) {
      setErr('Sign in to pin this listing + commute for later.');
      return;
    }
    if (!resolved) {
      setErr('Run a commute check first, then pin.');
      return;
    }
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/saved/${propertyId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            commute_anchor: { label: resolved.label,
                                lat: resolved.lat, lng: resolved.lng },
          }),
        },
      );
      if (!r.ok) throw new Error('save_failed');
      setSaved(true);
      setSavedAnchor({ label: resolved.label, lat: resolved.lat, lng: resolved.lng });
    } catch (e) { setErr('Could not pin. Try again.'); }
  };

  const clearDest = () => { setDest(''); setRoutes(null); setTransitRow(null); setDriveTraffic(null); setResolved(null); setErr(null); setSuggests(null); };

  return (
    <div className="rounded-lg ring-1 ring-gray-200 bg-white p-3" data-testid="commute-widget">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-2">
        <Route className="h-3.5 w-3.5" /> Commute check
      </div>
      <div className="flex gap-1.5">
        <div className="relative flex-1">
          <input
            type="text"
            value={dest}
            onChange={(e) => { setDest(e.target.value); setSuggestOpen(true); }}
            onFocus={() => setSuggestOpen(true)}
            onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
            onKeyDown={(e) => e.key === 'Enter' && run()}
            placeholder="e.g. Trinity College Dublin"
            className="w-full h-8 text-xs pl-2 pr-6 rounded border border-gray-200 focus:border-[#7042cc] focus:outline-none"
            data-testid="commute-dest-input"
            autoComplete="off"
          />
          {dest && (
            <button
              type="button" onClick={clearDest}
              aria-label="Clear commute destination"
              data-testid="commute-dest-clear"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
            >
              ×
            </button>
          )}
          {/* Autocomplete dropdown — Nominatim biased to the property's country */}
          {suggestOpen && Array.isArray(suggests) && suggests.length > 0 && (
            <ul
              data-testid="commute-suggests"
              className="absolute left-0 right-0 top-full mt-1 z-40 bg-white ring-1 ring-gray-200 rounded-md shadow-lg max-h-56 overflow-y-auto"
            >
              {suggests.map((s, i) => {
                const label = _shortLabel(s.display_name);
                const parts = (s.display_name || '').split(',').map((p) => p.trim());
                const rest  = parts.slice(3).join(', ');
                return (
                  <li key={s.place_id || i}
                       onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                       data-testid={`commute-suggest-${i}`}
                       className="px-2 py-1.5 text-xs cursor-pointer hover:bg-[#f7f3ff]">
                    <div className="text-gray-900 truncate">{label}</div>
                    {rest && <div className="text-[10px] text-gray-500 truncate">{rest}</div>}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <button
          type="button" onClick={run} disabled={loading || !dest.trim()}
          className="h-8 px-3 text-xs font-semibold rounded bg-[#7042cc] text-white hover:bg-[#5b35a8] disabled:opacity-50 flex items-center gap-1"
          data-testid="commute-run-btn"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Navigation className="h-3 w-3" />}
          Go
        </button>
      </div>

      {/* Recent-destination chips — one tap to jump back to a saved
          anchor like "work" vs "school". */}
      {recent.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2" data-testid="commute-recent-chips">
          {recent.map((q, i) => (
            <button
              key={q}
              type="button"
              onClick={() => pickRecent(q)}
              data-testid={`commute-recent-${i}`}
              className={`text-[10px] px-2 py-0.5 rounded-full ring-1 transition
                            ${dest.toLowerCase() === q.toLowerCase()
                              ? 'bg-[#7042cc] text-white ring-[#7042cc]'
                              : 'bg-white text-gray-700 ring-gray-200 hover:ring-[#7042cc]/60 hover:text-[#7042cc]'}`}
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {dest && !routes && !err && (
        <p className="text-[10px] text-gray-400 mt-1.5" data-testid="commute-sticky-hint">
          Destination remembered across listings — clear with × to reset.
        </p>
      )}
      {err && <p className="text-[11px] text-rose-600 mt-2">{err}</p>}
      {resolved && (
        <div className="mt-1.5 leading-tight" data-testid="commute-resolved-hint">
          <p className="text-[10px] text-gray-500">
            Matched to: <span className="text-gray-700">{resolved.label}</span>
            {altMatches.length > 1 && (
              <>
                {' · '}
                <button
                  type="button"
                  onClick={() => setAltOpen((v) => !v)}
                  className="text-[#5b35a8] font-semibold hover:underline"
                  data-testid="commute-alt-toggle"
                >
                  Not this place?
                </button>
              </>
            )}
          </p>
          {altOpen && altMatches.length > 1 && (
            <ul className="mt-1 rounded-md ring-1 ring-gray-200 bg-white shadow-sm overflow-hidden"
                 data-testid="commute-alt-list">
              {altMatches.map((alt, i) => {
                const shortLbl = _shortLabel(alt.display_name);
                const isCurrent = resolved.label === shortLbl;
                return (
                  <li key={`${alt.place_id || i}`}>
                    <button
                      type="button"
                      onClick={() => pickAltMatch(alt)}
                      disabled={isCurrent}
                      className={`w-full text-left px-2 py-1.5 text-[11px] flex items-start gap-2 ${
                        isCurrent
                          ? 'bg-[#f5efff] text-[#5b35a8] cursor-default'
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                      data-testid={`commute-alt-${i}`}
                    >
                      <span className="font-mono text-[10px] text-gray-400 shrink-0 mt-0.5">
                        {i + 1}.
                      </span>
                      <span className="leading-tight">{alt.display_name}</span>
                      {isCurrent && <span className="ml-auto text-[9px] uppercase tracking-wide">Current</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      {routes && (
        <ul className="mt-2 space-y-1" data-testid="commute-routes">
          {routes.map((r) => {
            // Only decorate the Drive row with HERE's live-traffic view.
            const isDrive = r.key === 'car';
            const tr = isDrive ? driveTraffic : null;
            const congested = tr && tr.congestion != null && tr.congestion > 0.2;
            const extraMin = tr && tr.free_minutes != null
              ? Math.max(0, tr.minutes - tr.free_minutes) : null;
            return (
              <li key={r.key} className="flex flex-col text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">{r.emoji} {r.label}</span>
                  <span className="font-mono text-gray-900">
                    {r.minutes != null ? `${r.minutes} min · ${r.km} km` : '—'}
                  </span>
                </div>
                {tr && tr.minutes != null && (
                  <div className="flex items-center justify-between mt-0.5 pl-4"
                        data-testid="commute-drive-traffic">
                    <span className={`text-[10px] ${congested ? 'text-amber-700' : 'text-gray-500'}`}>
                      🚦 Rush hour {congested ? '(heavy)' : ''}
                    </span>
                    <span className={`font-mono text-[11px] ${
                      congested ? 'font-bold text-amber-800' : 'text-gray-600'
                    }`}>
                      {tr.minutes} min
                      {extraMin != null && extraMin > 0 ? ` (+${extraMin} min)` : ''}
                    </span>
                  </div>
                )}
              </li>
            );
          })}
          {transitRow && (
            <li className="flex items-center justify-between text-xs"
                 data-testid="commute-row-transit">
              <span className="text-gray-700">🚇 Transit</span>
              <span className="font-mono text-gray-900">
                {transitRow.minutes != null
                  ? `${transitRow.minutes} min${transitRow.km ? ` · ${transitRow.km} km` : ''}`
                  : transitRow.unreachable ? 'no route' : '—'}
              </span>
            </li>
          )}
        </ul>
      )}

      {/* Pin listing + commute — saves the property to the buyer's
          Saved list with the current commute anchor attached, so
          tomorrow's session hydrates straight back to this destination. */}
      {routes && propertyId && (
        <button
          type="button"
          onClick={pinToListing}
          disabled={saved && savedAnchor?.label === resolved?.label}
          data-testid="commute-pin-listing"
          className="w-full mt-2 h-7 text-[11px] font-medium rounded
                      ring-1 ring-[#7042cc]/30 text-[#7042cc]
                      hover:bg-[#7042cc]/5 disabled:opacity-70
                      flex items-center justify-center gap-1"
        >
          {saved && savedAnchor?.label === resolved?.label
            ? <>✓ Saved — opens here next time</>
            : <>Save listing + this commute</>}
        </button>
      )}
    </div>
  );
};

// ── Optional polygon draw layer (draw-a-shape) ──────────────────────────
// Freehand tracer: user drags their mouse (desktop) or finger (mobile)
// across the map and we capture the path as a polygon. Falls back to
// click-to-add-vertex if the user prefers point-by-point mode.
const PolygonDraw = ({ enabled, points, setPoints }) => {
  const map = useMap();
  const [drawing, setDrawing] = useState(false);
  const [livePath, setLivePath] = useState([]);

  // Drop points closer than this many CSS pixels apart — keeps the
  // polygon vertex count manageable (~30-80 pts for a typical suburb
  // trace) so the backend ray-cast stays cheap.
  const MIN_PX_DIST = 6;
  const MIN_PX_DIST_SQ = MIN_PX_DIST * MIN_PX_DIST;

  // Show a crosshair cursor + disable map panning while draw mode is
  // on, so touch/drag becomes a tracing motion rather than pan.
  useEffect(() => {
    if (!enabled) return undefined;
    const container = map.getContainer();
    const prevCursor = container.style.cursor;
    container.style.cursor = 'crosshair';
    // Draw mode locks pan/box-select so drags trace the shape.
    map.dragging.disable();
    if (map.boxZoom)   map.boxZoom.disable();
    if (map.tap)       map.tap.disable();  // Leaflet mobile helper
    return () => {
      container.style.cursor = prevCursor;
      map.dragging.enable();
      if (map.boxZoom) map.boxZoom.enable();
      if (map.tap)     map.tap.enable();
    };
  }, [enabled, map]);

  // Native mouse + touch handlers on the map container. We use native
  // pointer/touch events (not Leaflet's) so drawing stays smooth on
  // mobile and doesn't clash with tap-to-place-marker semantics.
  useEffect(() => {
    if (!enabled) return undefined;
    const container = map.getContainer();
    let path = [];
    let lastPx = null;

    const containerPointFromEvent = (evt) => {
      const rect = container.getBoundingClientRect();
      const src = evt.touches?.[0] || evt.changedTouches?.[0] || evt;
      return { x: src.clientX - rect.left, y: src.clientY - rect.top };
    };

    const pushIfFarEnough = (px) => {
      if (lastPx) {
        const dx = px.x - lastPx.x;
        const dy = px.y - lastPx.y;
        if (dx * dx + dy * dy < MIN_PX_DIST_SQ) return false;
      }
      lastPx = px;
      const ll = map.containerPointToLatLng([px.x, px.y]);
      path.push([ll.lat, ll.lng]);
      return true;
    };

    const onStart = (evt) => {
      // Only left click on desktop; ignore multi-touch on mobile.
      if (evt.type === 'mousedown' && evt.button !== 0) return;
      if (evt.type === 'touchstart' && evt.touches.length > 1) return;
      evt.preventDefault();
      const px = containerPointFromEvent(evt);
      path = [];
      lastPx = null;
      pushIfFarEnough(px);
      setLivePath(path.slice());
      setDrawing(true);
    };

    const onMove = (evt) => {
      if (!path.length) return;
      // preventDefault stops the page from scrolling while the finger
      // is tracing — critical on iOS Safari where touchmove otherwise
      // hijacks the gesture as a scroll.
      evt.preventDefault();
      const px = containerPointFromEvent(evt);
      if (pushIfFarEnough(px)) setLivePath(path.slice());
    };

    const onEnd = () => {
      if (!path.length) return;
      // Need at least 3 vertices to form a polygon.
      if (path.length >= 3) setPoints(path.slice());
      path = [];
      lastPx = null;
      setLivePath([]);
      setDrawing(false);
    };

    // Order matters: touch first so browsers that fire both suppress
    // the synthetic mousedown that follows.
    container.addEventListener('touchstart', onStart, { passive: false });
    container.addEventListener('touchmove',  onMove,  { passive: false });
    container.addEventListener('touchend',   onEnd);
    container.addEventListener('touchcancel', onEnd);
    container.addEventListener('mousedown', onStart);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onEnd);

    return () => {
      container.removeEventListener('touchstart', onStart);
      container.removeEventListener('touchmove',  onMove);
      container.removeEventListener('touchend',   onEnd);
      container.removeEventListener('touchcancel', onEnd);
      container.removeEventListener('mousedown', onStart);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onEnd);
    };
  }, [enabled, map, setPoints]);

  if (!enabled) return null;

  // While the user is actively tracing → render the in-progress
  // polyline. When they lift → render the closed, shaded polygon.
  if (drawing && livePath.length >= 2) {
    return (
      <Polyline positions={livePath}
                 pathOptions={{ color: '#7042cc', weight: 3, opacity: 0.9,
                                 dashArray: '5 5', lineCap: 'round' }} />
    );
  }
  if (!drawing && points.length >= 3) {
    return (
      <Polygon positions={points}
               pathOptions={{ color: '#7042cc', weight: 2,
                               fillColor: '#7042cc', fillOpacity: 0.12 }} />
    );
  }
  return null;
};

// ── Heatmap overlay ────────────────────────────────────────────────────
// Aggregates every active amenity layer into a single density heatmap.
// Mounted via a child component so we get access to the Leaflet map
// instance via useMap().
const HeatmapLayer = ({ points }) => {
  const map = useMap();
  const layerRef = useRef(null);
  useEffect(() => {
    if (!map) return;
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }
    if (!points || points.length === 0) return;
    // leaflet.heat expects [[lat, lng, intensity], …]. Fixed intensity
    // (0.6) with radius 28 gives a soft-but-readable gradient that
    // doesn't wash out the underlying map tile.
    const heat = L.heatLayer(
      points.map((p) => [p[0], p[1], 0.6]),
      {
        radius:   28,
        blur:     22,
        maxZoom:  17,
        gradient: {
          0.2: '#16a34a',  // emerald  (low)
          0.5: '#f59e0b',  // amber    (medium)
          0.8: '#dc2626',  // red      (dense)
        },
      },
    );
    heat.addTo(map);
    layerRef.current = heat;
    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map, points]);
  return null;
};

// ── Main map component ─────────────────────────────────────────────────
const PropertyMap = ({
  property,
  location,
  height = '420px',
  zoom = 14,
  showNearby = false,
  showAmenities = false,
  showCommute = false,
  showNeighbourhoodAI = false,
  showSoldPrices = false,   // grey pins for comparable sold homes
  drawEnabled = false,
  onPolygonChange = null,
  propertyId = null,
}) => {
  // 1) Direct lat/lng on the record wins.
  const directCoords = useMemo(() => {
    const lat = location?.lat ?? location?.latitude;
    const lng = location?.lng ?? location?.longitude;
    if (typeof lat === 'number' && typeof lng === 'number') return [lat, lng];
    return null;
  }, [location?.lat, location?.lng, location?.latitude, location?.longitude]);

  const staticCoords = useMemo(() => staticLookup(location || {}), [location]);

  // 2) UK postcode → postcodes.io live lookup.
  const [liveCoords, setLiveCoords] = useState(null);
  useEffect(() => {
    if (directCoords || staticCoords) return;
    if (!UK_NAMES.test(location?.country || '')) return;
    const pc = extractUKPostcode(location || {});
    if (!pc) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc)}`);
        if (!res.ok) return;
        const data = await res.json();
        const r = data?.result;
        if (cancelled || !r) return;
        if (typeof r.latitude === 'number' && typeof r.longitude === 'number') {
          setLiveCoords([r.latitude, r.longitude]);
        }
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [directCoords, staticCoords, location]);

  const [lat, lng] = directCoords || staticCoords || liveCoords || fallbackCenter(location || {});

  // Nearby listings.
  const [nearby, setNearby] = useState([]);
  useEffect(() => {
    if (!showNearby || !lat || !lng) return;
    const params = new URLSearchParams({
      lat: String(lat), lng: String(lng), radius_km: '2',
      country: location?.country || 'Ireland',
      exclude_id: property?._id || property?.id || '',
    });
    fetch(`${BACKEND_URL}/api/properties/nearby?${params.toString()}`)
      .then((r) => r.ok ? r.json() : { items: [] })
      .then((d) => setNearby(d.items || []))
      .catch(() => setNearby([]));
  }, [showNearby, lat, lng, location?.country, property?._id, property?.id]);

  // Sold-price pins — toggleable. Postal-code clustered, so we jitter
  // around the property centre (±0.004° ≈ 400 m) rather than pretend
  // to know exact positions. Deterministic seeded jitter so pins stay
  // in place across renders.
  const [soldOn, setSoldOn] = useState(false);
  const [soldPins, setSoldPins] = useState([]);
  useEffect(() => {
    if (!showSoldPrices || !soldOn || !lat || !lng) { setSoldPins([]); return; }
    const uk = UK_NAMES.test(location?.country || '');
    const params = new URLSearchParams({ country: location?.country || 'Ireland', limit: '25' });
    if (uk) {
      if (location?.postcode) params.set('postcode', location.postcode);
      else { setSoldPins([]); return; }
    } else {
      if (property?.eircode) params.set('eircode', property.eircode);
      if (location?.county) params.set('county', location.county);
      if (location?.area)   params.set('area', location.area);
    }
    fetch(`${BACKEND_URL}/api/sold-prices/nearby?${params.toString()}`)
      .then((r) => r.ok ? r.json() : { items: [] })
      .then((d) => {
        const items = d.items || [];
        // Deterministic per-address jitter — used only when a row has
        // no stored lat/lng yet (pre-backfill). Backfilled rows keep
        // their true coordinates.
        const jitterFor = (addr) => {
          let h = 0;
          for (let i = 0; i < addr.length; i++) h = (h * 31 + addr.charCodeAt(i)) >>> 0;
          const jx = (((h >>> 0) & 0xffff) / 0xffff - 0.5) * 0.008;
          const jy = (((h >>> 16) & 0xffff) / 0xffff - 0.5) * 0.008;
          return { jx, jy };
        };
        setSoldPins(items.map((it, i) => {
          if (typeof it.lat === 'number' && typeof it.lng === 'number') {
            return { ...it, approximate: false };
          }
          const { jx, jy } = jitterFor((it.address || '') + i);
          return { ...it, lat: lat + jy, lng: lng + jx, approximate: true };
        }));
      })
      .catch(() => setSoldPins([]));
  }, [showSoldPrices, soldOn, lat, lng, location?.country, location?.postcode, location?.county, location?.area, property?.eircode]);

  // Amenities per category — lazily fetched on toggle.
  const [amenityLayers, setAmenityLayers] = useState({});   // { schools: [...] }
  const [amenityLoading, setAmenityLoading] = useState({}); // { schools: true }
  // Heatmap mode — when on, active amenity categories render as an
  // aggregated density gradient instead of individual pins. Buyers
  // get a "vibe at a glance" view without the pin clutter.
  const [heatmapOn, setHeatmapOn] = useState(false);
  // School catchment overlay — US shows the official NCES district
  // polygon; IE/UK shows an *estimated* Voronoi diagram over nearby
  // schools with a clear disclaimer badge.
  const [catchmentOn, setCatchmentOn] = useState(false);
  const [catchmentData, setCatchmentData] = useState(null);
  // Country hint used by the catchment endpoint. We surface the
  // property's country to the parent via a prop the same way we do
  // for market-scoped tile choices elsewhere.
  const catchmentCountry = location?.country || '';
  const toggleAmenity = useCallback(async (cat) => {
    if (amenityLayers[cat]) {
      setAmenityLayers((s) => { const n = { ...s }; delete n[cat]; return n; });
      return;
    }
    setAmenityLoading((s) => ({ ...s, [cat]: true }));
    const items = await fetchOverpass(lat, lng, cat);
    setAmenityLayers((s) => ({ ...s, [cat]: items }));
    setAmenityLoading((s) => ({ ...s, [cat]: false }));
  }, [amenityLayers, lat, lng]);

  // Polygon draw state (used on /buy).
  const [polyPoints, setPolyPoints] = useState([]);
  const setPolyPointsAndPush = useCallback((pts) => {
    setPolyPoints(pts);
    if (onPolygonChange) onPolygonChange(pts);
  }, [onPolygonChange]);
  const clearPolygon = () => setPolyPointsAndPush([]);

  const currency = currencyFor(location?.country);
  const fmtPrice = (v) => v != null ? `${currency}${Math.round(v).toLocaleString('en')}` : '';

  return (
    <div className="space-y-3" data-testid="property-map-enhanced">
      {/* Toolbar — only rendered when there's at least one interactive layer. */}
      {(showAmenities || drawEnabled || showSoldPrices) && (
        <div className="flex flex-wrap items-center gap-2">
          {showAmenities && Object.entries(AMENITY_CATS).map(([key, cat]) => {
            const Icon = cat.icon;
            const on   = !!amenityLayers[key];
            const busy = !!amenityLoading[key];
            return (
              <button
                key={key} type="button" onClick={() => toggleAmenity(key)}
                className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-semibold ring-1 transition-colors ${on
                  ? 'bg-[#7042cc] text-white ring-[#7042cc]'
                  : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50'}`}
                data-testid={`map-amenity-${key}`}
                aria-pressed={on}
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" style={{ color: on ? '#fff' : cat.colour }} />}
                {cat.label}
              </button>
            );
          })}
          {/* Heatmap toggle — swaps active amenity chips from pins to
              an aggregated density gradient. Only meaningful when at
              least one amenity chip is on, so we disable otherwise. */}
          {showAmenities && (
            <button
              type="button"
              onClick={() => setHeatmapOn((v) => !v)}
              disabled={Object.keys(amenityLayers).length === 0}
              className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-semibold ring-1 transition-colors ${heatmapOn
                ? 'bg-rose-600 text-white ring-rose-600'
                : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'}`}
              data-testid="map-heatmap-toggle"
              aria-pressed={heatmapOn}
              title={Object.keys(amenityLayers).length === 0
                ? 'Turn on an amenity chip first'
                : (heatmapOn ? 'Switch back to pins' : 'Show density heatmap')}
            >
              <Flame className="h-3 w-3" />
              Heatmap
            </button>
          )}
          {/* School catchment overlay — shows the NCES-official
              district polygon in the US, or an *estimated* Voronoi
              approximation over nearby schools in IE/UK with a
              disclaimer. */}
          {showAmenities && (
            <button
              type="button"
              onClick={() => setCatchmentOn((v) => !v)}
              className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-semibold ring-1 transition-colors ${catchmentOn
                ? 'bg-[#7042cc] text-white ring-[#7042cc]'
                : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50'}`}
              data-testid="map-catchment-toggle"
              aria-pressed={catchmentOn}
              title="Show the school catchment area for this address"
            >
              <MapPinned className="h-3 w-3" />
              School catchment area
            </button>
          )}
          {showSoldPrices && (
            <button
              type="button" onClick={() => setSoldOn((v) => !v)}
              className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-semibold ring-1 transition-colors ${soldOn
                ? 'bg-gray-700 text-white ring-gray-700'
                : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50'}`}
              data-testid="map-sold-toggle"
              aria-pressed={soldOn}
              title="Show comparable sold homes (clustered by postcode, position approximate)"
            >
              <span className="w-2 h-2 rounded-full inline-block" style={{ background: soldOn ? '#fff' : '#6b7280' }} />
              Sold nearby
            </button>
          )}
          {drawEnabled && (
            <>
              <span className="text-[11px] text-gray-500 ml-2">
                {polyPoints.length > 0
                  ? `Area drawn (${polyPoints.length} pts). Drag again to redraw, or Clear.`
                  : '↓ Click-and-drag your finger or mouse across the map to trace an area ↓'}
              </span>
              {polyPoints.length > 0 && (
                <button
                  type="button" onClick={clearPolygon}
                  className="inline-flex items-center h-7 px-2.5 rounded-full text-[11px] font-semibold ring-1 bg-white text-rose-600 ring-rose-200 hover:bg-rose-50"
                  data-testid="map-clear-polygon"
                >
                  Clear
                </button>
              )}
            </>
          )}
        </div>
      )}

      <div className={`grid gap-3 ${(showNeighbourhoodAI || showCommute) ? 'lg:grid-cols-[1fr_320px]' : ''}`}>
        <div style={{ height, width: '100%', borderRadius: '8px', overflow: 'hidden', position: 'relative' }}>
          {/* Coord quality badge — surfaces when the pin came from a
              coarse fallback (county centroid or Eircode routing key)
              rather than a street-exact geocoder. Sits top-left so it
              doesn't collide with Leaflet's default zoom controls. */}
          {(() => {
            const src = (location?.coords_source || '').toLowerCase();
            const isCoarse = src === 'county_centroid' || src === 'eircode_routing_key';
            if (!isCoarse) return null;
            const label = src === 'county_centroid' ? 'County area' : 'Postcode area';
            return (
              <div
                className="absolute top-2 left-12 z-[500]
                            inline-flex items-center gap-1
                            bg-amber-50 ring-1 ring-amber-300
                            text-amber-800 text-[10px] font-semibold
                            px-2 py-1 rounded-full shadow-sm"
                data-testid="coord-quality-badge"
                title={`Pin shows the ${label.toLowerCase()} — the exact address wasn't confirmed by our mapping data.`}
              >
                <Info className="h-3 w-3" />
                Approximate location · {label}
              </div>
            );
          })()}
          {/* Legend — bottom-left card, only shown when at least one
              amenity/sold layer is active. Explains each pin colour so
              buyers don't have to click a pin to know what it is. */}
          {(Object.keys(amenityLayers).length > 0 || (showSoldPrices && soldOn) || (showNearby && nearby.length > 0)) && (
            <div className="absolute bottom-2 left-2 z-[500] bg-white/95 backdrop-blur rounded-lg shadow-md ring-1 ring-gray-200 px-2.5 py-2 text-[10px] max-w-[180px]"
                  data-testid="map-legend">
              <div className="font-bold uppercase tracking-wider text-gray-500 mb-1">
                {heatmapOn ? 'Heatmap' : 'Legend'}
              </div>
              {heatmapOn ? (
                <div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="h-2 flex-1 rounded"
                           style={{ background: 'linear-gradient(90deg,#16a34a,#f59e0b,#dc2626)' }} />
                  </div>
                  <div className="flex justify-between text-gray-500">
                    <span>Low</span><span>High</span>
                  </div>
                  <div className="mt-1 text-gray-600">
                    Aggregating {Object.keys(amenityLayers).length} layer{Object.keys(amenityLayers).length === 1 ? '' : 's'}
                  </div>
                </div>
              ) : (
                <ul className="space-y-1">
                  {showNearby && nearby.length > 0 && (
                    <li className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full ring-2 ring-white shadow" style={{ background: '#7042cc' }} />
                      <span className="text-gray-700">Nearby homes</span>
                    </li>
                  )}
                  {Object.keys(amenityLayers).map((cat) => (
                    <li key={cat} className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full ring-2 ring-white shadow"
                             style={{ background: AMENITY_CATS[cat].colour }} />
                      <span className="text-gray-700">{AMENITY_CATS[cat].label}</span>
                    </li>
                  ))}
                  {showSoldPrices && soldOn && (
                    <li className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full ring-2 ring-white shadow" style={{ background: '#6b7280' }} />
                      <span className="text-gray-700">Sold nearby</span>
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}
          <MapContainer
            center={[lat, lng]}
            zoom={zoom}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={false}
          >
            <Recenter lat={lat} lng={lng} zoom={zoom} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* Primary property pin — hidden in draw-only mode (no
                specific listing to point at). */}
            {!drawEnabled && (
              <Marker position={[lat, lng]} icon={primaryIcon}>
                <Popup>
                  <div className="text-sm">
                    <p className="font-semibold">{location?.area || 'This property'}</p>
                    <p className="text-gray-600">{location?.city}, {location?.county}</p>
                    <p className="text-gray-600">{location?.country || 'Ireland'}</p>
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Nearby lovinahome listings */}
            {showNearby && nearby.map((n) => (
              (n.lat && n.lng) ? (
                <Marker key={n.id} position={[n.lat, n.lng]} icon={nearbyIcon}>
                  <Popup>
                    <div className="text-sm w-52" data-testid={`nearby-popup-${n.id}`}>
                      {n.image && (
                        <img src={n.image} alt={n.title}
                             className="w-full h-24 object-cover rounded mb-1.5" />
                      )}
                      <p className="font-semibold text-[13px] leading-tight">{n.title}</p>
                      <p className="text-[12px] text-gray-600">
                        {n.bedrooms ? `${n.bedrooms} bed · ` : ''}{n.propertyType || ''}
                      </p>
                      <p className="text-[13px] font-bold text-[#7042cc] mt-1">{fmtPrice(n.price)}</p>
                      <a href={`/property/${n.id}`}
                          className="inline-block mt-1 text-[11px] font-semibold text-[#5b35a8] hover:underline">
                        View listing →
                      </a>
                    </div>
                  </Popup>
                </Marker>
              ) : null
            ))}

            {/* Amenities — pins by default, aggregated heatmap when toggled */}
            {heatmapOn && Object.keys(amenityLayers).length > 0 && (
              <HeatmapLayer
                points={Object.values(amenityLayers).flat()
                  .filter((it) => it.lat && it.lng)
                  .map((it) => [it.lat, it.lng])}
              />
            )}
            {!heatmapOn && Object.entries(amenityLayers).map(([cat, items]) => (
              items.map((it) => (
                <Marker
                  key={it.id}
                  position={[it.lat, it.lng]}
                  icon={amenityIcon(AMENITY_CATS[cat].colour, cat)}
                  eventHandlers={{
                    mouseover: (e) => e.target.openPopup(),
                  }}
                >
                  <Popup maxWidth={260} minWidth={220} autoPan={false}>
                    <div className="text-xs w-56" data-testid={`amenity-popup-${it.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-[13px] leading-tight">{it.name}</p>
                        <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full ring-1 ring-gray-200 text-gray-600 whitespace-nowrap">
                          {AMENITY_CATS[cat].label}
                        </span>
                      </div>
                      {it._distM != null && (
                        <p className="text-[11px] text-[#7042cc] font-semibold mt-0.5">
                          {_fmtDist(it._distM)}
                        </p>
                      )}
                      {it.meta && it.meta.length > 0 && (
                        <p className="text-[11px] text-gray-700 mt-1">{it.meta.join(' · ')}</p>
                      )}
                      {it.address && (
                        <p className="text-[11px] text-gray-600 mt-1 flex items-start gap-1">
                          <MapPin className="h-3 w-3 mt-0.5 shrink-0 text-gray-400" />
                          <span>{it.address}</span>
                        </p>
                      )}
                      {it.hours && (
                        <p className="text-[11px] text-gray-600 mt-1 flex items-start gap-1">
                          <Clock className="h-3 w-3 mt-0.5 shrink-0 text-gray-400" />
                          <span className="font-mono">{it.hours}</span>
                        </p>
                      )}
                      {it.phone && (
                        <p className="text-[11px] text-gray-600 mt-1 flex items-center gap-1">
                          <Phone className="h-3 w-3 shrink-0 text-gray-400" />
                          <a href={`tel:${it.phone.replace(/\s+/g, '')}`}
                              className="text-[#5b35a8] hover:underline">
                            {it.phone}
                          </a>
                        </p>
                      )}
                      {it.website && (
                        <p className="text-[11px] text-gray-600 mt-1 flex items-center gap-1">
                          <Globe className="h-3 w-3 shrink-0 text-gray-400" />
                          <a href={it.website} target="_blank" rel="noopener noreferrer"
                              className="text-[#5b35a8] hover:underline truncate max-w-[180px]">
                            {it.website.replace(/^https?:\/\/(www\.)?/, '')}
                          </a>
                        </p>
                      )}
                      <div className="flex gap-2 mt-2 pt-2 border-t border-gray-100">
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${it.lat},${it.lng}`}
                          target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#5b35a8] hover:underline"
                          data-testid={`amenity-gmaps-${it.id}`}
                        >
                          Google Maps <ExternalLink className="h-2.5 w-2.5" />
                        </a>
                        <a
                          href={`https://www.openstreetmap.org/${it.osmType || 'node'}/${it.osmId}`}
                          target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#5b35a8] hover:underline"
                          data-testid={`amenity-osm-${it.id}`}
                        >
                          OpenStreetMap <ExternalLink className="h-2.5 w-2.5" />
                        </a>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))
            ))}

            {/* Sold-price pins — grey, jittered around property centre */}
            {showSoldPrices && soldOn && soldPins.map((p, i) => (
              <Marker key={`sold-${i}`} position={[p.lat, p.lng]} icon={soldIcon}>
                <Popup>
                  <div className="text-xs w-48" data-testid={`sold-popup-${i}`}>
                    <p className="font-semibold text-[13px] leading-tight">{p.address}</p>
                    <p className="text-gray-600 mt-1">
                      <span className="font-mono font-bold">
                        {p.currency || '€'}{p.price != null ? Math.round(p.price).toLocaleString('en') : '—'}
                      </span>
                      {p.date && <span className="ml-2 text-[11px] text-gray-500">
                        {new Date(p.date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                      </span>}
                    </p>
                    {p.approximate && (
                      <p className="text-[10px] text-gray-400 mt-1 italic">
                        Postcode-clustered · position approximate
                      </p>
                    )}
                    <p className="text-[10px] text-gray-500 mt-1">{p.source}</p>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Optional polygon-draw layer */}
            <PolygonDraw enabled={drawEnabled} points={polyPoints} setPoints={setPolyPointsAndPush} />

            {/* School catchment overlay */}
            <CatchmentOverlay
              lat={lat}
              lng={lng}
              country={catchmentCountry}
              active={catchmentOn}
              onData={setCatchmentData}
            />
          </MapContainer>
          {/* Disclaimer/legend outside MapContainer so it stacks above
              the tiles without competing with Leaflet controls. */}
          <CatchmentDisclaimer data={catchmentData} active={catchmentOn} />
        </div>

        {/* Side panel — AI blurb + commute */}
        {(showNeighbourhoodAI || showCommute) && (
          <div className="space-y-3">
            {showNeighbourhoodAI && location?.area && (
              <NeighbourhoodAI
                area={location.area}
                county={location.county}
                country={location.country}
                beds={property?.bedrooms}
                propertyType={property?.propertyType}
              />
            )}
            {showCommute && (
              <CommuteWidget lat={lat} lng={lng}
                              country={location?.country}
                              propertyId={propertyId} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyMap;
